/** Seconds of clock skew allowed before treating a JWT as expired. */
const EXPIRY_SKEW_SEC = 30;

export interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/** Decode JWT payload (no signature verification - client-side expiry hint only). */
export function parseJwtPayload(accessToken: string): JwtPayload | null {
  const parts = accessToken.split(".");
  const payloadPart = parts[1];
  if (parts.length < 2 || !payloadPart) return null;
  try {
    const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const json = atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** True if the token is missing, unparseable, or past exp (with skew). */
export function isAccessTokenExpired(accessToken: string | null | undefined): boolean {
  if (!accessToken?.trim()) return true;
  const payload = parseJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== "number") return true;
  const nowSec = Math.floor(Date.now() / 1000);
  return payload.exp <= nowSec + EXPIRY_SKEW_SEC;
}

/** Seconds until JWT exp (negative if already expired). Null if unparseable. */
export function secondsUntilExpiry(accessToken: string | null | undefined): number | null {
  if (!accessToken?.trim()) return null;
  const payload = parseJwtPayload(accessToken);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp - Math.floor(Date.now() / 1000);
}
